/**
 * `/organization`, the workspaces of the caller's own organization (TECH-3200).
 *
 * A thin wrapper, like `/platform/workspaces`. The panel is shared
 * (`components/workspaces/workspace-admin-panel.tsx`) and this file owns only
 * which procedures answer it. What is absent from every call below is an
 * organization id. The gate resolves the tenant from the caller, so there is
 * nothing here to point at somebody else's organization.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "sonner";
import { ulid } from "ulid";
import { WorkspaceAdminPanel } from "@/components/workspaces/workspace-admin-panel";
import { useWorkspaceList } from "@/hooks/use-workspace-list";
import { ws } from "@/lib/websocket-wrapper";

export const Route = createFileRoute("/organization/")({
	component: OrganizationWorkspacesPage,
});

function OrganizationWorkspacesPage() {
	const { workspaces, loading, load } = useWorkspaceList(
		"organization.listWorkspaces"
	);

	useEffect(() => {
		load();
	}, []);

	async function handleCreate(name: string) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "organization.createWorkspace",
				data: { name },
			});
			toast.success(`Created workspace ${name}`);
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Create failed: ${message}`);
			// Rethrown so the panel leaves the typed name in place. A refused name is
			// usually one edit away from a good one.
			throw err;
		}
	}

	async function handleRename(workspaceId: string, name: string) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "organization.renameWorkspace",
				data: { workspaceId, name },
			});
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Rename failed: ${message}`);
			throw err;
		}
	}

	async function handleSetEnabled(workspaceId: string, enabled: boolean) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "organization.setWorkspaceEnabled",
				data: { workspaceId, enabled },
			});
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Update failed: ${message}`);
			throw err;
		}
	}

	async function handleSetExternalRef(
		workspaceId: string,
		externalRef: string | null
	) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "organization.setWorkspaceExternalRef",
				data: { workspaceId, externalRef },
			});
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Update failed: ${message}`);
			throw err;
		}
	}

	return (
		<div className="flex flex-col items-center gap-8 p-8">
			<WorkspaceAdminPanel
				loading={loading}
				onCreate={handleCreate}
				onRename={handleRename}
				onSetEnabled={handleSetEnabled}
				onSetExternalRef={handleSetExternalRef}
				workspaces={workspaces}
			/>
		</div>
	);
}
